import React, { useEffect, useState } from "react";
import {
  Box,
  Typography,
  CircularProgress,
  Alert,
  Grid,
  Card,
  CardMedia,
  CardContent,
  CardActions,
  Button,
  Chip,
  Avatar,
  Divider,
  IconButton,
  Tooltip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  List,
  ListItem,
  ListItemText,
} from "@mui/material";
import { getAvailablePlans } from "../../../Services/SystemExplanationsApi";
import { API_BASE_URL_images } from "../../../Services/apiConfig";
import LocalOfferIcon from "@mui/icons-material/LocalOffer";
import AttachMoneyIcon from "@mui/icons-material/AttachMoney";
import CalendarMonthIcon from "@mui/icons-material/CalendarMonth";
import EventAvailableIcon from "@mui/icons-material/EventAvailable";
import DiscountIcon from "@mui/icons-material/Discount";
import VisibilityIcon from "@mui/icons-material/Visibility";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import ImageIcon from "@mui/icons-material/Image";
import StarIcon from "@mui/icons-material/Star";
import CloseIcon from "@mui/icons-material/Close";

const WebsitePlansTap = () => {
  const [plans, setPlans] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selectedPlan, setSelectedPlan] = useState(null); // <-- new state

  useEffect(() => {
    const fetchPlans = async () => {
      try {
        const response = await getAvailablePlans();
        const data = response?.data?.data?.palnes; // ✅ تعديل هنا

        if (Array.isArray(data)) {
          // احتفظ فقط بالخطط النشطة (status === 1)
          const activePlans = data.filter((p) => Number(p.status) === 1);

          // فرز تصاعدي حسب plane_order، العناصر بدون قيمة تذهب للنهاية
          activePlans.sort((a, b) => {
            const A =
              a?.plane_order != null
                ? Number(a.plane_order)
                : Number.MAX_SAFE_INTEGER;
            const B =
              b?.plane_order != null
                ? Number(b.plane_order)
                : Number.MAX_SAFE_INTEGER;
            return A - B;
          });

          setPlans(activePlans);
        } else {
          console.warn("Invalid plans data format:", response);
          setPlans([]);
        }
      } catch (error) {
        console.error("Error fetching plans:", error);
        setError("فشل في جلب بيانات الخطط");
      } finally {
        setLoading(false);
      }
    };

    fetchPlans();
  }, []);

  return (
    <Box>
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          mb: 3,
        }}
      >
        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
          <LocalOfferIcon sx={{ fontSize: 32, color: "#1976d2" }} />
          <Typography
            variant="h5"
            sx={{ fontWeight: "bold", color: "#333" }}
          >
            الخطط المتاحة للعملاء
          </Typography>
        </Box>
        {!loading && !error && (
          <Chip
            label={`${plans.length} خطة`}
            color="primary"
            sx={{ fontWeight: "bold", fontSize: "1rem" }}
          />
        )}
      </Box>

      {loading ? (
        <Box
          sx={{
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            py: 8,
          }}
        >
          <CircularProgress size={60} />
          <Typography variant="h6" sx={{ ml: 2 }}>
            جاري تحميل الخطط...
          </Typography>
        </Box>
      ) : error ? (
        <Alert
          severity="error"
          sx={{
            fontSize: "1.1rem",
            py: 3,
            borderRadius: 2,
          }}
        >
          {error}
        </Alert>
      ) : plans.length > 0 ? (
        <Grid container spacing={3}>
          {plans.map((plan, index) => (
            <Grid item xs={12} sm={6} md={4} key={plan.id}>
              <Card
                elevation={4}
                sx={{
                  height: "100%",
                  display: "flex",
                  flexDirection: "column",
                  transition: "all 0.3s",
                  position: "relative",
                  overflow: "visible",
                  borderRadius: 3,
                  "&:hover": {
                    transform: "translateY(-12px)",
                    boxShadow: 8,
                  },
                }}
              >
                {/* Badge للخصم */}
                {plan.descount > 0 && (
                  <Box
                    sx={{
                      position: "absolute",
                      top: -10,
                      right: 20,
                      bgcolor: "#f44336",
                      color: "white",
                      borderRadius: "50%",
                      width: 70,
                      height: 70,
                      display: "flex",
                      flexDirection: "column",
                      alignItems: "center",
                      justifyContent: "center",
                      boxShadow: 3,
                      zIndex: 1,
                      animation: "pulse 2s infinite",
                      "@keyframes pulse": {
                        "0%, 100%": { transform: "scale(1)" },
                        "50%": { transform: "scale(1.05)" },
                      },
                    }}
                  >
                    <DiscountIcon sx={{ fontSize: 20 }} />
                    <Typography
                      variant="h6"
                      sx={{ fontWeight: "bold", lineHeight: 1 }}
                    >
                      {plan.descount}%
                    </Typography>
                  </Box>
                )}

                {/* الصورة */}
                {plan.image ? (
                  <CardMedia
                    component="img"
                    height="200"
                    image={`${API_BASE_URL_images}/${plan.image}`}
                    alt={plan.plane_name}
                    sx={{
                      objectFit: "cover",
                      borderBottom: "4px solid #1976d2",
                    }}
                  />
                ) : (
                  <Box
                    sx={{
                      height: 200,
                      bgcolor: "#f5f5f5",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      borderBottom: "4px solid #1976d2",
                    }}
                  >
                    <ImageIcon sx={{ fontSize: 80, color: "#bdbdbd" }} />
                  </Box>
                )}

                <CardContent sx={{ flexGrow: 1, pt: 3 }}>
                  {/* اسم الخطة */}
                  <Box
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      gap: 1,
                      mb: 2,
                    }}
                  >
                    <Avatar sx={{ bgcolor: "#1976d2" }}>
                      <StarIcon />
                    </Avatar>
                    <Typography
                      variant="h5"
                      sx={{ fontWeight: "bold", color: "#333" }}
                    >
                      {plan.plane_name}
                    </Typography>
                  </Box>

                  <Divider sx={{ mb: 2 }} />

                  {/* سعر الخطة الرئيسي */}
                  {(() => {
                    const original = Number(plan.price) || 0;
                    const discount = Number(plan.descount) || 0;
                    const after =
                      Math.round((original * (1 - discount / 100)) * 100) / 100;
                    return (
                      <Box sx={{ mb: 3, textAlign: "center" }}>
                        {discount > 0 ? (
                          <>
                            <Box
                              sx={{
                                display: "flex",
                                justifyContent: "center",
                                alignItems: "baseline",
                                gap: 1,
                              }}
                            >
                              <Typography
                                variant="h3"
                                sx={{
                                  fontWeight: 900,
                                  color: "#e65100",
                                }}
                              >
                                {after}
                              </Typography>
                              <Typography
                                variant="h6"
                                sx={{ color: "text.secondary" }}
                              >
                                جنيه
                              </Typography>
                            </Box>
                            <Typography
                              variant="caption"
                              sx={{
                                display: "block",
                                color: "text.secondary",
                                textDecoration: "line-through",
                                mt: 0.5,
                              }}
                            >
                              السعر الأصلي: {original} جنيه
                            </Typography>
                          </>
                        ) : (
                          <Box
                            sx={{
                              display: "flex",
                              justifyContent: "center",
                              alignItems: "baseline",
                              gap: 1,
                            }}
                          >
                            <Typography
                              variant="h4"
                              sx={{ fontWeight: 800, color: "#e65100" }}
                            >
                              {original}
                            </Typography>
                            <Typography
                              variant="h6"
                              sx={{ color: "text.secondary" }}
                            >
                              جنيه
                            </Typography>
                          </Box>
                        )}
                      </Box>
                    );
                  })()}

                  {/* الوصف */}
                  <Typography
                    variant="body2"
                    color="text.secondary"
                    sx={{
                      mb: 2,
                      minHeight: 60,
                      lineHeight: 1.6,
                    }}
                  >
                    {plan.desc}
                  </Typography>

                  {/* السعر الشهري */}
                  <Box
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      gap: 1,
                      mb: 1.5,
                      p: 1.5,
                      bgcolor: "#e3f2fd",
                      borderRadius: 2,
                    }}
                  >
                    <CalendarMonthIcon sx={{ color: "#1976d2" }} />
                    <Typography
                      variant="body2"
                      sx={{ flex: 1, fontWeight: "bold" }}
                    >
                      السعر الشهري:
                    </Typography>
                    <Chip
                      icon={<AttachMoneyIcon />}
                      label={`${plan.monthly_price}$`}
                      color="primary"
                      sx={{ fontWeight: "bold" }}
                    />
                  </Box>

                  {/* السعر السنوي */}
                  <Box
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      gap: 1,
                      p: 1.5,
                      bgcolor: "#f3e5f5",
                      borderRadius: 2,
                    }}
                  >
                    <EventAvailableIcon sx={{ color: "#9c27b0" }} />
                    <Typography
                      variant="body2"
                      sx={{ flex: 1, fontWeight: "bold" }}
                    >
                      السعر السنوي:
                    </Typography>
                    <Chip
                      icon={<AttachMoneyIcon />}
                      label={`${plan.yearly_price}$`}
                      color="secondary"
                      sx={{ fontWeight: "bold" }}
                    />
                  </Box>

                  {/* حالة الخطة */}
                  {plan.status === 1 && (
                    <Box
                      sx={{
                        mt: 2,
                        display: "flex",
                        justifyContent: "center",
                      }}
                    >
                      <Chip
                        icon={<CheckCircleIcon />}
                        label="متاحة للاشتراك"
                        color="success"
                        size="small"
                      />
                    </Box>
                  )}
                </CardContent>

                <Divider />

                <CardActions
                  sx={{
                    p: 2,
                    justifyContent: "center",
                    bgcolor: "#fafafa",
                  }}
                >
                  <Button
                    size="large"
                    variant="contained"
                    fullWidth
                    startIcon={<VisibilityIcon />}
                    onClick={() => setSelectedPlan(plan)}
                    sx={{
                      borderRadius: 2,
                      py: 1.5,
                      fontWeight: "bold",
                      background:
                        "linear-gradient(45deg, #2196F3 30%, #21CBF3 90%)",
                      "&:hover": {
                        background:
                          "linear-gradient(45deg, #1976d2 30%, #1e88e5 90%)",
                        transform: "scale(1.02)",
                        transition: "all 0.2s",
                      },
                    }}
                  >
                    عرض التفاصيل الكاملة
                  </Button>
                </CardActions>
              </Card>
            </Grid>
          ))}
        </Grid>
      ) : (
        <Box
          sx={{
            textAlign: "center",
            py: 8,
            bgcolor: "#f5f5f5",
            borderRadius: 2,
          }}
        >
          <LocalOfferIcon sx={{ fontSize: 80, color: "#bdbdbd", mb: 2 }} />
          <Typography variant="h6" color="text.secondary">
            لا توجد خطط متوفرة حالياً
          </Typography>
        </Box>
      )}

      {/* Dialog عرض تفاصيل الخطة والمميزات */}
      <Dialog
        open={!!selectedPlan}
        onClose={() => setSelectedPlan(null)}
        maxWidth="md"
        fullWidth
        PaperProps={{ sx: { borderRadius: 2 } }}
      >
        <DialogTitle sx={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
          <Typography variant="h6" sx={{ fontWeight: "bold" }}>{selectedPlan?.plane_name}</Typography>
          <IconButton onClick={() => setSelectedPlan(null)}>
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent dividers>
          {selectedPlan ? (
            <>
              <Typography variant="subtitle2" sx={{ mb: 2 }}>{selectedPlan.desc}</Typography>
              <Divider sx={{ mb: 2 }} />
              <Typography variant="h6" sx={{ mb: 1 }}>المميزات ({(selectedPlan.features || []).length})</Typography>
              {(selectedPlan.features || []).length === 0 ? (
                <Typography color="text.secondary">لا توجد مميزات لهذه الخطة.</Typography>
              ) : (
                <List>
                  {(selectedPlan.features || []).map((f) => (
                    <ListItem key={f.id} alignItems="flex-start" sx={{ py: 1 }}>
                      <ListItemText
                        primary={<Typography sx={{ fontWeight: 700 }}>{f.name}</Typography>}
                        secondary={
                          <>
                            <Typography variant="caption" color="text.secondary" display="block">المفتاح: {f.key}</Typography>
                            <Typography variant="caption" color="text.secondary" display="block">القيمة: {f.pivot?.value ?? "-"}</Typography>
                            <Typography variant="caption" color="text.secondary" display="block">شهري: {f.pivot?.monthly_cost ?? "-"}</Typography>
                            <Typography variant="caption" color="text.secondary" display="block">سنوي: {f.pivot?.yearly_cost ?? "-"}</Typography>
                          </>
                        }
                      />
                    </ListItem>
                  ))}
                </List>
              )}
            </>
          ) : null}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setSelectedPlan(null)} variant="contained">إغلاق</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default WebsitePlansTap;
